"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.dashboardResponse = exports.dashboardInsight = exports.dashboardLink = exports.insightSeverity = exports.insightRule = exports.dashboardQuery = exports.dashboardPeriodPreset = void 0;
const zod_1 = require("zod");
const common_1 = require("./common");
const period_1 = require("./period");
const report_catalog_1 = require("./report-catalog");
/** فترة اللوحة: الشهر أو الربع أو السنة المالية حتى التاريخ، أو من تاريخ يختاره المستخدم. */
exports.dashboardPeriodPreset = zod_1.z.enum(['month', 'quarter', 'ytd', 'custom']);
exports.dashboardQuery = zod_1.z
    .object({
    asOf: common_1.isoDate.optional(),
    period: exports.dashboardPeriodPreset.default('month'),
    /** بداية الفترة — لـ `custom` وحدها، وإلا تُحسب من التاريخ. */
    from: common_1.isoDate.optional(),
})
    .strict()
    .refine((query) => query.period !== 'custom' || query.from !== undefined, {
    message: 'custom period needs from',
    path: ['from'],
})
    .refine((query) => !query.from || !query.asOf || query.from <= query.asOf, {
    message: 'from must not be after asOf',
    path: ['from'],
});
/**
 * قواعد التنبيهات الذكية — كل قاعدة تقرأ أرقامًا منشورة وتقارنها بحد من إعدادات الشركة.
 * لا ذكاء خارجى: البيانات لا تغادر الخادم.
 */
exports.insightRule = zod_1.z.enum([
    'revenue_change',
    'expense_spike',
    'margin_change',
    'net_loss',
    'overdue_customer',
    'cash_runway',
    'slow_moving_stock',
    'vat_due',
    'large_unpaid_invoices',
    'supplier_due_soon',
    'best_sales_day',
]);
exports.insightSeverity = zod_1.z.enum(['critical', 'warning', 'info', 'good']);
/** أين يفتح التنبيه: التقرير أو الكشف الذى يأتى منه رقمه. */
exports.dashboardLink = zod_1.z.discriminatedUnion('screen', [
    zod_1.z.object({
        screen: zod_1.z.literal('report'),
        tab: zod_1.z.enum(['income', 'balance', 'aging', 'apAging', 'vat']),
    }),
    zod_1.z.object({ screen: zod_1.z.literal('customer'), customerId: common_1.uuid }),
    zod_1.z.object({ screen: zod_1.z.literal('supplier'), supplierId: common_1.uuid }),
    zod_1.z.object({ screen: zod_1.z.literal('ledger'), accountCode: common_1.accountCode }),
    zod_1.z.object({ screen: zod_1.z.literal('inventory') }),
    zod_1.z.object({ screen: zod_1.z.literal('treasuryAccounts') }),
    zod_1.z.object({ screen: zod_1.z.literal('reconciliation') }),
    /** تقرير بعينه فى مركز التقارير (معرّفات الفهرس) بفترته — أو الفهرس نفسه بلا تقرير. */
    zod_1.z.object({
        screen: zod_1.z.literal('reportsCenter'),
        report: zod_1.z
            .enum(['sales', 'purchases', 'treasury', 'debts', ...report_catalog_1.catalogReportId.options])
            .optional(),
        from: common_1.isoDate.optional(),
        to: common_1.isoDate.optional(),
        /** للأصناف الراكدة: أيام بلا بيع. */
        days: zod_1.z.number().int().optional(),
        groupBy: zod_1.z.enum(['customer', 'item']).optional(),
    }),
    zod_1.z.object({ screen: zod_1.z.literal('periods') }),
]);
/**
 * تنبيه واحد. النص العربى تصوغه الواجهة من القاعدة والأرقام، فالعقد أرقام لا جُمل:
 * `value` الرقم الرئيسى بوحدته (أو `growing` للنقدية المتزايدة)، و`amount` مبلغ مصاحب،
 * و`previous` قيمة المقارنة كما تعرّفها القاعدة.
 */
exports.dashboardInsight = zod_1.z.object({
    rule: exports.insightRule,
    severity: exports.insightSeverity,
    value: zod_1.z.string(),
    unit: zod_1.z.enum(['money', 'percent', 'points', 'months', 'count', 'days']),
    amount: common_1.moneyString.nullable(),
    previous: zod_1.z.string().nullable(),
    subject: zod_1.z.object({ id: zod_1.z.string().nullable(), name: zod_1.z.string() }).nullable(),
    count: zod_1.z.number().int().nullable(),
    date: common_1.isoDate.nullable(),
    link: exports.dashboardLink,
});
const treasuryBalance = zod_1.z.object({
    id: common_1.uuid,
    nameAr: zod_1.z.string(),
    accountCode: common_1.accountCode,
    isActive: zod_1.z.boolean(),
    balance: common_1.moneyString,
});
const periodFigures = zod_1.z.object({
    from: common_1.isoDate,
    to: common_1.isoDate,
    revenue: common_1.moneyString,
    /** تكلفة المبيعات + المصروفات التشغيلية كما فى قائمة الدخل. */
    expenses: common_1.moneyString,
    /** الإيراد − المصروفات (الربح التشغيلى) — البنود الأخرى خارجه. */
    operatingProfit: common_1.moneyString,
    /** الإيراد − تكلفة المبيعات. */
    grossProfit: common_1.moneyString,
    /** صافى الربح كما فى قائمة الدخل: التشغيلى + البنود الأخرى. */
    netProfit: common_1.moneyString,
});
const partyExposure = zod_1.z.object({
    /** صافى المستحق كما فى تقرير الأعمار (بعد خصم الدائن غير المخصص). */
    total: common_1.moneyString,
    /** ما تجاوز استحقاقه: مجموع الشرائح بعد «الحالية». */
    overdue: common_1.moneyString,
});
/** مؤشر بقيمته وقيمة فترة المقارنة وخط صغير لآخر ١٢ شهرًا (الأقدم أولاً). */
const kpi = zod_1.z.object({
    value: common_1.moneyString,
    previous: common_1.moneyString,
    sparkline: zod_1.z.array(common_1.moneyString),
});
/** نسبة مئوية بمنزلتين، أو null حين لا إيراد يُقسم عليه. */
const percentString = zod_1.z.string().regex(/^-?\d+(\.\d{1,2})?$/);
/** شرائح أعمار بمسمياتها فى تقريرى الأعمار — للعملاء والموردين معًا. */
const agingBuckets = zod_1.z.object({
    current: common_1.moneyString,
    days1to30: common_1.moneyString,
    days31to60: common_1.moneyString,
    days61to90: common_1.moneyString,
    over90: common_1.moneyString,
    /** تحصيلات (أو سدادات) لم تُخصص بعد — تُخصم من الإجمالى. */
    unappliedCredit: common_1.moneyString,
});
const ranked = zod_1.z.object({
    key: zod_1.z.string(),
    label: zod_1.z.string(),
    amount: common_1.moneyString,
    link: exports.dashboardLink,
});
/**
 * اللوحة الرئيسية فى تاريخ — قراءة واحدة تجمع ما يحتاجه المالك أول ما يفتح البرنامج.
 * كل رقم هنا يُحسب من نفس مصدر تقريره (الميزان، الأعمار، قائمة الدخل، المطابقة) لا من
 * رصيد مخزَّن، والتحقق الحى يتأكد من التساوى.
 */
exports.dashboardResponse = zod_1.z.object({
    companyId: common_1.uuid,
    asOf: common_1.isoDate,
    /** الفترة المعروضة وفترة المقارنة كما حُسبتا من الإعداد. */
    period: zod_1.z.object({
        preset: exports.dashboardPeriodPreset,
        from: common_1.isoDate,
        to: common_1.isoDate,
        previousFrom: common_1.isoDate,
        previousTo: common_1.isoDate,
        comparison: zod_1.z.enum(['previous_period', 'same_period_last_year']),
    }),
    cash: zod_1.z.object({
        /** رصيد كل حسابات الأستاذ ذات دفتر خزينة أو بنك/محفظة حتى التاريخ. */
        total: common_1.moneyString,
        cashboxes: zod_1.z.array(treasuryBalance),
        bankAccounts: zod_1.z.array(treasuryBalance),
        /** ما على حسابات النقدية والبنوك وليس مربوطًا بخزينة أو حساب بنكى (قيود يدوية مثلاً). */
        unassigned: common_1.moneyString,
    }),
    receivables: partyExposure,
    payables: partyExposure,
    /** مؤشرات الفترة: التدفقات للفترة، والأرصدة فى نهايتها، والمقارنة بنهاية فترة المقارنة. */
    kpis: zod_1.z.object({
        revenue: kpi,
        grossProfit: kpi,
        grossMargin: zod_1.z.object({
            value: percentString.nullable(),
            previous: percentString.nullable(),
            sparkline: zod_1.z.array(percentString.nullable()),
        }),
        netProfit: kpi,
        cash: kpi,
        receivables: kpi,
        payables: kpi,
        /** بلا خط صغير: أعمار كل نهاية شهر تعنى إعادة حساب الأعمار ١٢ مرة. */
        overdueReceivables: kpi,
        /** رصيد حسابات المخزون فى الأستاذ — رقم الميزانية نفسه. */
        inventory: kpi,
    }),
    thisMonth: periodFigures,
    lastMonth: periodFigures,
    /** آخر ١٢ شهرًا حتى التاريخ، الأقدم أولاً. */
    monthly: zod_1.z.array(periodFigures.extend({ month: zod_1.z.string().regex(/^\d{4}-\d{2}$/) })),
    /**
     * المبيعات والمشتريات بالمستندات لكل شهر من الشهور نفسها: صافى المبيعات وصافى المشتريات
     * (بعد الإلغاء والإشعارات، بلا ضريبة) كما فى تحليلى المبيعات والمشتريات بمركز التقارير.
     */
    trade: zod_1.z.array(zod_1.z.object({
        month: zod_1.z.string().regex(/^d{4}-d{2}$/),
        sales: common_1.moneyString,
        purchases: common_1.moneyString,
    })),
    /** رصيد النقدية والبنوك فى نهاية كل شهر من الشهور نفسها. */
    cashTrend: zod_1.z.array(zod_1.z.object({ month: zod_1.z.string().regex(/^\d{4}-\d{2}$/), balance: common_1.moneyString })),
    /** الأكبر فى الفترة: العملاء بصافى المبيعات، والأصناف بصافى المبيعات، والمصروفات بالحساب. */
    top: zod_1.z.object({
        customers: zod_1.z.array(ranked),
        items: zod_1.z.array(ranked),
        expenses: zod_1.z.array(ranked),
    }),
    /** شرائح أعمار العملاء فى التاريخ — نفس إجماليات تقرير الأعمار. */
    receivablesAging: agingBuckets,
    /** شرائح أعمار الموردين فى التاريخ — نفس إجماليات تقرير أعمار الموردين. */
    payablesAging: agingBuckets,
    insights: zod_1.z.array(exports.dashboardInsight),
    /** مهام اليوم: ما ينتظر عملاً من المستخدم. */
    todos: zod_1.z.object({
        /** قيود محفوظة غير منشورة. المستندات اليوم تُنشر عند الحفظ، فهذا صفر عادةً. */
        draftJournals: zod_1.z.number().int(),
        overdueInvoices: zod_1.z.number().int(),
        billsDueSoon: zod_1.z.number().int(),
        /** فترات انتهت قبل الشهر الحالى ولم تُقفل. */
        periodsToClose: zod_1.z.number().int(),
    }),
    /** سطور منشورة على الحسابات البنكية حتى التاريخ لم تدخل أى مطابقة. */
    unreconciledBankLines: zod_1.z.number().int(),
    /** الفترة المالية التى تحتوى التاريخ، أو null إن لم تُعرَّف. */
    currentPeriod: zod_1.z
        .object({ id: common_1.uuid, startDate: common_1.isoDate, endDate: common_1.isoDate, status: period_1.periodStatus })
        .nullable(),
    /** شركة بلا أى قيد منشور حتى التاريخ — الواجهة تعرض البداية بدل أرقام صفرية. */
    isEmpty: zod_1.z.boolean(),
});
//# sourceMappingURL=dashboard.js.map